import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import EmailCompose from '../components/adminEmails/EmailCompose';
import EmailDetail from '../components/adminEmails/EmailDetail';
import EmailFilters from '../components/adminEmails/EmailFilters';
import EmailGroups from '../components/adminEmails/EmailGroups';
import EmailList from '../components/adminEmails/EmailList';
import EmailLogs from '../components/adminEmails/EmailLogs';
import EmailSetupNote from '../components/adminEmails/EmailSetupNote';
import EmailUnsubscribes from '../components/adminEmails/EmailUnsubscribes';
import { DEFAULT_CC, getEmailFilterQuery, parseRecipientPreview } from '../components/adminEmails/emailUtils';
import {
  archiveAdminEmail,
  addAdminEmailUnsubscribe,
  bulkSendAdminEmail,
  createAdminEmailGroup,
  deleteAdminEmailGroup,
  deleteAdminEmailUnsubscribe,
  getAdminEmailLogs,
  getAdminEmailBulkSendStatus,
  getAdminEmailGroups,
  getAdminEmailUnsubscribes,
  getAdminEmails,
  removeAdminEmailFromGroup,
  replyAdminEmail,
  sendAdminEmail,
  updateAdminEmailGroup,
  updateAdminEmailReadStatus,
} from '../services/adminService';

const initialForm = {
  senderKey: 'factorstreet_admin',
  mailbox: 'admin',
  toText: '',
  ccText: DEFAULT_CC,
  bccText: '',
  subject: '',
  body: '',
  emailType: 'general',
  recipientSource: 'manual',
  groupId: '',
  attachments: [],
};

const initialGroupForm = {
  name: '',
  description: '',
  emailsText: '',
};

const initialUnsubscribeForm = {
  email: '',
  reason: '',
};

const fallbackSenders = [];
const ADMINFACTOR_EMAIL_URL =
  'https://adminfactor.com/sites/1997f354-5920-4061-9a26-63b46a9c1d85/email';

const pollDelayMs = 2000;
const searchDebounceMs = 400;
const fallbackBulkLimit = 500;
const fallbackBulkDelayMs = 250;

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getApiErrorMessage(error, fallback) {
  const retryAfter = error.response?.data?.retryAfter;
  const message = error.response?.data?.message || error.message || fallback;

  if (error.response?.status === 429 && retryAfter) {
    return `${message} Try again in ${retryAfter} seconds.`;
  }

  return message || fallback;
}

function normalizeEmailValue(value) {
  return String(value || '').trim().toLowerCase();
}

function BulkSendSummary({ summary }) {
  if (!summary) return null;

  const failures = summary.failures || [];
  const skipped = summary.skippedUnsubscribed || summary.skippedUnsubscribedEmails || [];

  return (
    <div className="admin-panel admin-email-send-summary">
      <h2>Bulk send summary</h2>
      <div className="bulk-preview-grid">
        <span>Sent: {summary.successCount || summary.sent || 0}</span>
        <span>Failed: {summary.failedCount || summary.failed || 0}</span>
        <span>Skipped unsubscribed: {skipped.length || summary.skippedUnsubscribedCount || 0}</span>
        <span>Invalid: {summary.invalidEmails?.length || summary.invalid || 0}</span>
        <span>Total: {summary.totalRecipients || summary.total || 0}</span>
      </div>
      {failures.length ? (
        <div className="admin-email-failed-list">
          <strong>Failed recipients</strong>
          {failures.slice(0, 30).map((failure) => (
            <span key={`${failure.email}-${failure.message}`}>
              {failure.email}: {failure.message}
            </span>
          ))}
          {failures.length > 30 ? <span>+{failures.length - 30} more failed recipients</span> : null}
        </div>
      ) : null}
      {failures.length ? <p>Bulk retries are disabled.</p> : null}
    </div>
  );
}

function AdminEmails() {
  const { emailId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isComposeRoute = location.pathname.endsWith('/compose');
  const [emails, setEmails] = useState([]);
  const [logs, setLogs] = useState([]);
  const [unsubscribes, setUnsubscribes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState('inbox');
  const [activeTab, setActiveTab] = useState('mail');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEmailId, setSelectedEmailId] = useState(emailId || '');
  const [form, setForm] = useState(initialForm);
  const [groupForm, setGroupForm] = useState(initialGroupForm);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [unsubscribeForm, setUnsubscribeForm] = useState(initialUnsubscribeForm);
  const [replyBody, setReplyBody] = useState('');
  const [replyCc, setReplyCc] = useState(DEFAULT_CC);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkSendJob, setBulkSendJob] = useState(null);
  const [lastSendSummary, setLastSendSummary] = useState(null);
  const sendingMode = 'single';
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const emailRequestRef = useRef(0);

  const selectedEmail = useMemo(
    () => emails.find((email) => email._id === selectedEmailId) || null,
    [emails, selectedEmailId]
  );
  const selectedGroup = useMemo(
    () => groups.find((group) => group._id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );
  const senderOptions = settings?.senders?.length ? settings.senders : fallbackSenders;
  const defaultSender = senderOptions[0] || null;
  const defaultCc = settings?.defaultCc || DEFAULT_CC;
  const bulkLimit = settings?.bulkLimit || fallbackBulkLimit;
  const bulkDelayMs = settings?.bulkSend?.delayMs ?? fallbackBulkDelayMs;

  const loadEmails = useCallback(async () => {
    const requestId = emailRequestRef.current + 1;
    emailRequestRef.current = requestId;
    let data;

    try {
      data = await getAdminEmails({ ...getEmailFilterQuery(filter), search: debouncedSearch });
    } catch (error) {
      if (requestId !== emailRequestRef.current) {
        return null;
      }

      throw error;
    }

    if (requestId !== emailRequestRef.current) {
      return null;
    }

    setEmails(data.emails || []);
    setSettings(data.settings || null);
    return data;
  }, [debouncedSearch, filter]);

  const loadLogs = useCallback(async () => {
    const logData = await getAdminEmailLogs();
    setLogs(logData.logs || []);
    return logData;
  }, []);

  const loadUnsubscribes = useCallback(async () => {
    const unsubscribeData = await getAdminEmailUnsubscribes();
    setUnsubscribes(unsubscribeData.unsubscribes || []);
    return unsubscribeData;
  }, []);

  const loadGroups = useCallback(async () => {
    const groupData = await getAdminEmailGroups();
    setGroups(groupData.groups || []);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    loadEmails()
      .then((data) => {
        if (isMounted && data !== null) setLoading(false);
      })
      .catch((error) => {
        if (isMounted) {
          setLoading(false);
          setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to load emails.') });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [loadEmails]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (activeTab !== 'logs') return;
    loadLogs().catch((error) => {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to load email logs.') });
    });
  }, [activeTab, loadLogs]);

  useEffect(() => {
    if (activeTab !== 'groups' && !(activeTab === 'mail' && isComposeRoute)) return;
    loadGroups().catch((error) => {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to load email groups.') });
    });
  }, [activeTab, isComposeRoute, loadGroups]);

  useEffect(() => {
    if (activeTab !== 'unsubscribes' && !(activeTab === 'mail' && isComposeRoute)) return;
    loadUnsubscribes().catch((error) => {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to load unsubscribes.') });
    });
  }, [activeTab, isComposeRoute, loadUnsubscribes]);

  useEffect(() => {
    if (emailId) setSelectedEmailId(emailId);
  }, [emailId]);

  useEffect(() => {
    if (isComposeRoute || emailId) {
      setActiveTab('mail');
    }
  }, [emailId, isComposeRoute]);

  useEffect(() => {
    setReplyCc(defaultCc);
    setForm((current) =>
      !current.ccText || current.ccText === DEFAULT_CC ? { ...current, ccText: defaultCc } : current
    );
  }, [defaultCc]);

  useEffect(() => {
    if (!senderOptions.length) return;

    setForm((current) => {
      const currentSender = senderOptions.find((sender) => (sender.key || sender.senderKey) === current.senderKey);
      if (currentSender) return current;

      return {
        ...current,
        senderKey: defaultSender?.key || defaultSender?.senderKey || '',
        mailbox: defaultSender?.mailbox || current.mailbox,
      };
    });
  }, [defaultSender, senderOptions]);

  const changeFilter = (nextFilter) => {
    if (nextFilter === 'unsubscribes') {
      setActiveTab('unsubscribes');
      return;
    }

    setActiveTab('mail');
    setFilter(nextFilter);
  };

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: value };
      const selectedSender = name === 'senderKey' ? senderOptions.find((sender) => (sender.key || sender.senderKey) === value) : null;

      if (selectedSender) next.mailbox = selectedSender.mailbox || next.mailbox;
      if (name === 'recipientSource' && value === 'manual') next.groupId = '';
      if (name === 'recipientSource' && value === 'group') next.toText = '';

      return next;
    });
  };

  const resetForm = () => {
    setForm({
      ...initialForm,
      ccText: defaultCc,
      senderKey: defaultSender?.key || defaultSender?.senderKey || '',
      mailbox: defaultSender?.mailbox || '',
    });
    setBulkPreview(null);
  };

  const getBulkFinalRecipientCount = (payload) => {
    const unsubscribedSet = new Set(
      unsubscribes.map((record) => normalizeEmailValue(record.normalizedEmail || record.email)).filter(Boolean)
    );

    if (payload.recipientSource === 'group') {
      const group = groups.find((item) => item._id === payload.groupId);
      const uniqueEmails = Array.from(
        new Set((group?.emails || []).map((record) => normalizeEmailValue(record.normalizedEmail || record.email)).filter(Boolean))
      );

      return uniqueEmails.filter((email) => !unsubscribedSet.has(email)).length;
    }

    const preview = parseRecipientPreview(payload.toText);
    return preview.validEmails.filter((email) => !unsubscribedSet.has(email)).length;
  };

  const waitForBulkSendJob = async (jobId) => {
    while (true) {
      const data = await getAdminEmailBulkSendStatus(jobId);
      const job = data.job;
      setBulkSendJob(job);

      if (job.status === 'completed') {
        return job.result || job;
      }

      if (job.status === 'failed') {
        throw new Error(job.error || job.message || 'Bulk email send failed.');
      }

      await wait(pollDelayMs);
    }
  };

  const submitEmail = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    setLastSendSummary(null);

    try {
      const payload =
        sendingMode === 'bulk' && form.recipientSource === 'group'
          ? { ...form, toText: '', groupId: form.groupId }
          : { ...form, groupId: '' };

      if (sendingMode === 'bulk' && getBulkFinalRecipientCount(payload) > bulkLimit) {
        setFeedback({
          type: 'error',
          message: `Bulk send limit is ${bulkLimit} recipients per send. Please split into smaller groups.`,
        });
        return;
      }

      setSubmitting(true);
      const startResult = sendingMode === 'bulk' ? await bulkSendAdminEmail(payload) : await sendAdminEmail(payload);
      if (startResult.job) setBulkSendJob(startResult.job);
      const result =
        sendingMode === 'bulk' && startResult.job
          ? await waitForBulkSendJob(startResult.job.id)
          : startResult;

      await loadEmails();
      resetForm();
      setBulkSendJob(null);
      setLastSendSummary(sendingMode === 'bulk' ? result : null);
      setFeedback({
        type: 'success',
        message: `Sent: ${result.successCount || 0}. Failed: ${result.failedCount || 0}. Invalid skipped: ${
          result.invalidEmails?.length || 0
        }. Unsubscribed skipped: ${result.skippedUnsubscribed?.length || 0}.`,
      });
      navigate('/admin/emails');
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to send email.') });
    } finally {
      setSubmitting(false);
    }
  };

  const selectEmail = async (email) => {
    setSelectedEmailId(email._id);
    navigate(`/admin/emails/${email._id}`);

    if (email.readStatus !== 'unread') return;

    try {
      const result = await updateAdminEmailReadStatus(email._id, 'read');
      setEmails((current) => current.map((item) => (item._id === email._id ? result.email : item)));
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to mark email as read.') });
    }
  };

  const toggleReadStatus = async () => {
    if (!selectedEmail) return;
    const nextStatus = selectedEmail.readStatus === 'read' ? 'unread' : 'read';

    try {
      const result = await updateAdminEmailReadStatus(selectedEmail._id, nextStatus);
      setEmails((current) => current.map((item) => (item._id === selectedEmail._id ? result.email : item)));
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to update read status.') });
    }
  };

  const archiveSelectedEmail = async () => {
    if (!selectedEmail) return;

    try {
      const result = await archiveAdminEmail(selectedEmail._id);
      setEmails((current) => current.map((item) => (item._id === selectedEmail._id ? result.email : item)));
      setFeedback({ type: 'success', message: 'Email archived.' });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to archive email.') });
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selectedEmail) return;

    setSubmitting(true);
    try {
      const result = await replyAdminEmail(selectedEmail._id, {
        body: replyBody,
        ccText: replyCc,
      });
      await loadEmails();
      setReplyBody('');
      setFeedback({
        type: 'success',
        message: [`Reply sent. Failed: ${result.failedCount || 0}.`, ...(result.warnings || [])].join(' '),
      });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to send reply.') });
    } finally {
      setSubmitting(false);
    }
  };

  const updateUnsubscribeForm = (event) => {
    const { name, value } = event.target;
    setUnsubscribeForm((current) => ({ ...current, [name]: value }));
  };

  const addUnsubscribe = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      await addAdminEmailUnsubscribe(unsubscribeForm);
      await loadUnsubscribes();
      setUnsubscribeForm(initialUnsubscribeForm);
      setFeedback({ type: 'success', message: 'Email added to unsubscribe list.' });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to add unsubscribe.') });
    } finally {
      setSubmitting(false);
    }
  };

  const removeUnsubscribe = async (unsubscribeId) => {
    setSubmitting(true);

    try {
      await deleteAdminEmailUnsubscribe(unsubscribeId);
      await loadUnsubscribes();
      setFeedback({ type: 'success', message: 'Email removed from unsubscribe list.' });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to remove unsubscribe.') });
    } finally {
      setSubmitting(false);
    }
  };

  const updateGroupForm = (event) => {
    const { name, value } = event.target;
    setGroupForm((current) => ({ ...current, [name]: value }));
  };

  const selectGroup = (group) => {
    setSelectedGroupId(group._id);
    setGroupForm({
      name: group.name || '',
      description: group.description || '',
      emailsText: group.emails?.map((record) => (record.name ? `${record.name} <${record.email}>` : record.email)).join('\n') || '',
    });
  };

  const saveGroup = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const result = selectedGroup
        ? await updateAdminEmailGroup(selectedGroup._id, groupForm)
        : await createAdminEmailGroup(groupForm);
      await loadGroups();
      setSelectedGroupId(result.group?._id || selectedGroupId);
      setFeedback({
        type: 'success',
        message: `Group saved. Invalid: ${result.invalidEmails?.length || 0}. Duplicates removed: ${
          result.duplicateEmails?.length || 0
        }.`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to save group.') });
    } finally {
      setSubmitting(false);
    }
  };

  const deleteGroup = async (groupId) => {
    setSubmitting(true);

    try {
      await deleteAdminEmailGroup(groupId);
      await loadGroups();
      setSelectedGroupId('');
      setGroupForm(initialGroupForm);
      setFeedback({ type: 'success', message: 'Group deleted.' });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to delete group.') });
    } finally {
      setSubmitting(false);
    }
  };

  const removeEmailFromGroup = async (groupId, groupEmailId) => {
    setSubmitting(true);

    try {
      const result = await removeAdminEmailFromGroup(groupId, groupEmailId);
      await loadGroups();
      selectGroup(result.group);
      setFeedback({ type: 'success', message: 'Email removed from group.' });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Unable to remove email from group.') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="admin-page admin-emails-page">
      <div className="admin-page__header--split">
        <div>
          <p className="eyebrow">Emails</p>
          <h1>FactorStreet email desk</h1>
          <p>Send, receive, and reply to FactorStreet emails from one admin workspace.</p>
        </div>
        <a className="btn btn--primary" href={ADMINFACTOR_EMAIL_URL} rel="noreferrer" target="_blank">
          Open AdminFactor Email
        </a>
      </div>

      {feedback.message ? (
        <p className={`form-alert form-alert--${feedback.type === 'error' ? 'error' : 'success'}`}>
          {feedback.message}
        </p>
      ) : null}
      <BulkSendSummary summary={lastSendSummary} />

      <div className="admin-email-tabs">
        {['mail', 'groups', 'logs', 'unsubscribes', 'setup'].map((tab) => (
          <button
            className={activeTab === tab ? 'is-active' : ''}
            key={tab}
            onClick={() => setActiveTab(tab)}
            type="button"
          >
            {tab === 'mail' ? 'Mail' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'mail' ? (
        <div className="admin-email-layout">
          <aside className="admin-panel admin-email-sidebar">
            <EmailFilters activeTab={activeTab} filter={filter} onChange={changeFilter} />
            <EmailSetupNote settings={settings} />
          </aside>

          <div className="admin-email-center">
            <div className="admin-panel admin-email-search">
              <input
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search from, to, subject, or body"
                type="search"
                value={search}
              />
            </div>
            <EmailList emails={emails} loading={loading} onSelect={selectEmail} selectedEmailId={selectedEmailId} />
          </div>

          <div className="admin-email-detail-column">
            {isComposeRoute ? (
              senderOptions.length ? (
                <EmailCompose
                  bulkDelayMs={bulkDelayMs}
                  bulkLimit={bulkLimit}
                  bulkPreview={bulkPreview}
                  bulkSendJob={bulkSendJob}
                  form={form}
                  groups={groups}
                  onPreview={setBulkPreview}
                  onSubmit={submitEmail}
                  onUpdateForm={updateForm}
                  senderOptions={senderOptions}
                  sendingMode={sendingMode}
                  setBulkPreview={setBulkPreview}
                  submitting={submitting}
                  unsubscribes={unsubscribes}
                />
              ) : (
                <div className="admin-panel admin-email-detail">
                  <h2>FactorStreet Compose moved</h2>
                  <p>New FactorStreet correspondence is sent from the AdminFactor Email desk through AWS SES.</p>
                  <a className="btn btn--primary" href={ADMINFACTOR_EMAIL_URL} rel="noreferrer" target="_blank">
                    Open AdminFactor Email
                  </a>
                </div>
              )
            ) : (
              <EmailDetail
                onArchive={archiveSelectedEmail}
                onReply={sendReply}
                onToggleRead={toggleReadStatus}
                replyBody={replyBody}
                replyCc={replyCc}
                selectedEmail={selectedEmail}
                setReplyBody={setReplyBody}
                setReplyCc={setReplyCc}
                submitting={submitting}
                unsubscribes={unsubscribes}
              />
            )}
          </div>
        </div>
      ) : null}

      {activeTab === 'logs' ? <EmailLogs logs={logs} /> : null}
      {activeTab === 'groups' ? (
        <EmailGroups
          groupForm={groupForm}
          groups={groups}
          onCreate={saveGroup}
          onDelete={deleteGroup}
          onRemoveEmail={removeEmailFromGroup}
          onSelect={selectGroup}
          onUpdateForm={updateGroupForm}
          selectedGroup={selectedGroup}
          submitting={submitting}
        />
      ) : null}
      {activeTab === 'unsubscribes' ? (
        <EmailUnsubscribes
          addForm={unsubscribeForm}
          onAdd={addUnsubscribe}
          onDelete={removeUnsubscribe}
          onUpdateAddForm={updateUnsubscribeForm}
          submitting={submitting}
          unsubscribes={unsubscribes}
        />
      ) : null}
      {activeTab === 'setup' ? <EmailSetupNote settings={settings} /> : null}
    </section>
  );
}

export default AdminEmails;
